import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/salesOrderMutations"

import {
  type SalesOrder,
  SalesOrderStatus,
  type SalesOrderUpdateError,
  type SalesOrderUpdateFailure,
  type SalesOrderUpdateInput,
  UpdateSalesOrderDocument,
  type UpdateSalesOrderMutation,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { SALES_ORDER_MISSING_FIELD_TO_LABEL } from "@/screens/CustomerOrders/constants"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { PERMISSION_DENIED_MUTATION_MESSAGE, isForbiddenError } from "@/utils/urql/permissionError"

interface UpdateSalesOrderOptions {
  successMessage?: string
}

interface UseUpdateSalesOrderReturn {
  updateSalesOrder: (input: SalesOrderUpdateInput, options?: UpdateSalesOrderOptions) => Promise<SalesOrder | undefined>
  isSaving: boolean
}

const toFailureMessage = ({ message, missingFields }: SalesOrderUpdateError, input: SalesOrderUpdateInput): string => {
  const isConfirming = input.status === SalesOrderStatus.Confirmed
  if (!isConfirming || !missingFields?.length) return message

  const fieldLabels = missingFields.map((field) => SALES_ORDER_MISSING_FIELD_TO_LABEL[field] ?? field)
  return `${fieldLabels.join(", ")} required to confirm`
}

export const useUpdateSalesOrder = (): UseUpdateSalesOrderReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<UpdateSalesOrderMutation, { input: SalesOrderUpdateInput }>(
    UpdateSalesOrderDocument
  )

  const updateSalesOrder = useCallback(
    async (
      input: SalesOrderUpdateInput,
      { successMessage = "Changes saved" }: UpdateSalesOrderOptions = {}
    ): Promise<SalesOrder | undefined> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.salesOrder.update
      const hasError = error || output?.__typename === "SalesOrderUpdateFailure"

      if (hasError) {
        const message = isForbiddenError(error)
          ? PERMISSION_DENIED_MUTATION_MESSAGE
          : (error?.message ?? toFailureMessage((output as SalesOrderUpdateFailure).error, input))
        notify({ type: NotificationType.Error, message })
        return undefined
      }

      notify({ type: NotificationType.Success, message: successMessage })
      return output?.__typename === "SalesOrderUpdateSuccess" ? (output.salesOrder as SalesOrder) : undefined
    },
    [executeMutation, notify]
  )

  return { updateSalesOrder, isSaving: fetching }
}
